'use client';

import React, { useState } from 'react';

const STORAGE_KEY = 'admin_password';
const DEFAULT_PASSWORD = '123456';

interface AdminChangePasswordProps {
  onClose: () => void;
}

export default function AdminChangePassword({ onClose }: AdminChangePasswordProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  function getStoredPassword(): string {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_PASSWORD;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const stored = getStoredPassword();

    if (currentPassword !== stored) {
      setError('Current password is incorrect.');
      return;
    }

    if (!/^\d{6}$/.test(newPassword)) {
      setError('New password must be exactly 6 digits.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    localStorage.setItem(STORAGE_KEY, newPassword);
    setSuccess(true);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-sm shadow-xl">
        <h2 className="font-playfair text-xl font-semibold text-foreground mb-6">
          Change Password
        </h2>

        {success ? (
          <div className="space-y-4">
            <p className="text-sm text-green-400 font-lora">
              Password updated successfully.
            </p>
            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-semibold font-lora hover:bg-primary/90 transition"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5 font-lora">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground text-sm font-lora focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                placeholder="Current 6-digit password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5 font-lora">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                maxLength={6}
                className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground text-sm font-lora focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                placeholder="New 6-digit password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5 font-lora">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                maxLength={6}
                className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground text-sm font-lora focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                placeholder="Confirm new password"
              />
            </div>

            {error && (
              <p className="text-sm text-red-400 font-lora">{error}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 px-4 border border-border text-foreground rounded-lg text-sm font-semibold font-lora hover:bg-background/60 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-2.5 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-semibold font-lora hover:bg-primary/90 transition"
              >
                Update
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
